// BUILD-042 Slice A UAT correction (2026-10-02) — named Employee selection, replacing a raw
// UUID free-text input. Decision requirement #2: "Human-readable authorized names throughout
// employee lists, pickers, details... UUIDs may be secondary technical details, never the
// required ordinary user input." Backed by GET /hr/admin/employees (already enriched with
// canonical platform names — see platform-employee-directory.ts) — this component does not call
// any new endpoint and does not create any second identity source.
import { useEffect, useMemo, useRef, useState } from 'react';
import { listHrAdminEmployees, type HrAdminEmployeeListEntry } from '../../api/client';

interface Props {
  value: string;
  onChange: (employeeId: string) => void;
  /** When set, only candidates with hasHrRecord === filterHasHrRecord are offered. */
  filterHasHrRecord?: boolean;
  placeholder?: string;
}

export function EmployeePicker({ value, onChange, filterHasHrRecord, placeholder }: Props) {
  const [entries, setEntries] = useState<HrAdminEmployeeListEntry[] | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // The list closes 100 ms after the input loses focus (so a click on a list item still lands). Oct 20: that pending close is cancelled whenever the
  // input is used again; before, a quick second pick could have the previous blur's timer close the list that had just reopened.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPendingClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };

  useEffect(() => {
    listHrAdminEmployees().then((d) => setEntries(d.employees)).catch(() => setEntries([]));
    return () => cancelPendingClose();
  }, []);

  const candidates = useMemo(() => {
    const all = entries ?? [];
    const scoped = filterHasHrRecord === undefined ? all : all.filter((e) => e.hasHrRecord === filterHasHrRecord);
    const q = query.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter((e) =>
      (e.name ?? '').toLowerCase().includes(q) ||
      (e.email ?? '').toLowerCase().includes(q) ||
      e.employeeId.toLowerCase().includes(q)
    );
  }, [entries, query, filterHasHrRecord]);

  const selected = (entries ?? []).find((e) => e.employeeId === value) || null;

  return (
    <div className="relative flex-1 min-w-[240px]">
      <input
        className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full"
        placeholder={placeholder ?? 'Search by name…'}
        value={open ? query : (selected ? (selected.name ?? selected.email ?? selected.employeeId) : query)}
        onFocus={() => { cancelPendingClose(); setOpen(true); setQuery(''); }}
        onChange={(e) => { cancelPendingClose(); setOpen(true); setQuery(e.target.value); onChange(''); }}
        onBlur={() => { cancelPendingClose(); closeTimer.current = setTimeout(() => { closeTimer.current = null; setOpen(false); }, 100); }}
      />
      {open && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-56 overflow-auto">
          {entries === null && <div className="px-3 py-2 text-sm text-gray-400">Loading…</div>}
          {entries !== null && candidates.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400">No matching Employees.</div>
          )}
          {candidates.map((c) => (
            <button
              key={c.employeeId}
              type="button"
              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              onClick={() => { onChange(c.employeeId); setQuery(''); setOpen(false); }}
            >
              <div className="font-medium text-gray-900">{c.name ?? c.email ?? '(no name on file)'}</div>
              <div className="text-xs text-gray-400">
                {c.name && c.email ? `${c.email} · ` : ''}{c.employeeId}{c.hasHrRecord ? '' : ' · no HR record yet'}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
